import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../services/api";

export function useMessages(user) {
  const tokenHeader = { headers: { Authorization: "Bearer " + user?.token } };
  const qc = useQueryClient();

  const useMessagesQuery = (chatId) =>
    useQuery({
      queryKey: ["messages", chatId],
      queryFn: async () => {
        if (!chatId) return [];
        const res = await api.get(`/messages/chats/${chatId}`, tokenHeader);
        return res.data;
      },
      enabled: !!chatId,
      staleTime: 15_000,
    });

  const useFavoritesQuery = (chatId) =>
    useQuery({
      queryKey: ["favorites", chatId],
      queryFn: async () => {
        if (!chatId) return [];
        const res = await api.get(`/messages/chats/starred/${chatId}`, tokenHeader);
        return res.data;
      },
      enabled: !!chatId,
      staleTime: 30_000,
    });

  const fetchChatInfo = useCallback(async (chatId) => {
    const res = await api.get(`/chats/${chatId}`, tokenHeader);
    return res.data;
  }, [user?.token]);

  const fetchUserInfo = useCallback(async (userId) => {
    const res = await api.get(`/users/${userId}`, tokenHeader);
    return res.data;
  }, [user?.token]);

  const markRead = useCallback(async (chatId) => {
    await api.post(`/chats/readat/${chatId}`, { chat_id: chatId }, tokenHeader);
  }, [user?.token]);

  // Send helpers
  const sendText = useCallback(async (chatId, text) => {
    return await api.post(`/messages/send`, { chat_id: chatId, type: "text", body: text }, tokenHeader);
  }, [user?.token]);

  const sendReply = useCallback(async (replyId, chatId, text) => {
    return await api.post(`/messages/send/reply/${replyId}`, { chat_id: chatId, type: "text", body: text }, tokenHeader);
  }, [user?.token]);

  const sendImagePaste = useCallback(async (chatId, body, blob) => {
    return await api.post(`/messages/send`, { chat_id: chatId, type: "image", format: ".jpg", body, blob, mime_type: "image/jpeg" }, tokenHeader);
  }, [user?.token]);

  const sendFile = useCallback(async (chatId, type, body, blob, format, mime_type) => {
    return await api.post(`/messages/send`, { chat_id: chatId, type, body, blob, format, mime_type }, tokenHeader);
  }, [user?.token]);

  const invalidateMessages = useCallback((chatId) => {
    qc.invalidateQueries({ queryKey: ["messages", chatId] });
  }, [qc]);

  const invalidateFavorites = useCallback((chatId) => {
    qc.invalidateQueries({ queryKey: ["favorites", chatId] });
  }, [qc]);

  return {
    useMessagesQuery,
    useFavoritesQuery,
    fetchChatInfo,
    fetchUserInfo,
    markRead,
    sendText,
    sendReply,
    sendImagePaste,
    sendFile,
    invalidateMessages,
    invalidateFavorites,
  };
}
